import React, { useMemo } from 'react';
import BarrasHorizontais from '../shared/BarrasHorizontais';
import { agruparPorPeriodo, rotuloDePeriodo } from '../../../data/relatorios';
import { resumoDeSaidas, rotuloDoMotivo, enderecoDoBotijao } from '../../../data/estoque';
import { Destaque, Indicador, Cartao, Tabela, Vazio, num, dinheiro, BotaoPDF } from './Pecas';
import { gerarRelatorioPDF, descreverFiltros } from '../../../utils/relatorioPDF';

/**
 * Relatório de saídas: o que deixou o botijão sem ir para uma receptora.
 *
 * A métrica que lidera é a CONTAGEM, não o valor — ao contrário do relatório
 * de serviços. A maior parte das saídas não tem valor nenhum (descarte, perda,
 * devolução), e destacar dinheiro faria um mês de perdas parecer um mês fraco
 * de vendas.
 */
const PainelSaidas = ({ linhas, dimensao, granularidade, contexto = {} }) => {
  const t = useMemo(() => resumoDeSaidas(linhas), [linhas]);

  const porPeriodo = useMemo(
    () => agruparPorPeriodo(
      // `agruparPorPeriodo` espera o campo de data com o nome que recebe.
      linhas.map(l => ({ ...l, _valor_venda: l.motivo === 'venda' ? Number(l.valor) || 0 : 0 })),
      'saida_em', granularidade, ['_valor_venda']),
    [linhas, granularidade]);

  const porDimensao = useMemo(() => {
    const mapa = new Map();
    linhas.forEach(l => {
      const chave = l[dimensao] || '—';
      const a = mapa.get(chave) || { chave, embrioes: 0, valor: 0 };
      a.embrioes += 1;
      if (l.motivo === 'venda') a.valor += Number(l.valor) || 0;
      mapa.set(chave, a);
    });
    return [...mapa.values()].sort((a, b) => b.embrioes - a.embrioes);
  }, [linhas, dimensao]);

  const COLUNAS = [
          { chave: 'data', rotulo: 'Data',
            valor: (l) => (l.saida_em
              ? new Date(`${l.saida_em}T12:00:00`).toLocaleDateString('pt-BR') : '—') },
          { chave: 'motivo', rotulo: 'Motivo', valor: (l) => rotuloDoMotivo(l.motivo) },
          { chave: 'cli', rotulo: 'Cliente', valor: (l) => l.client_name || '—' },
          { chave: 'num', rotulo: 'Nº', numerica: true, valor: (l) => (l.numero ?? '—') },
          { chave: 'doadora', rotulo: 'Doadora', valor: (l) => l.doadora || '—' },
          { chave: 'touro', rotulo: 'Touro', valor: (l) => l.touro || '—' },
          { chave: 'destino', rotulo: 'Para quem', valor: (l) => l.destino || '—' },
          { chave: 'de_onde', rotulo: 'Saiu de',
            valor: (l) => enderecoDoBotijao(l) || '—' },
          { chave: 'quem', rotulo: 'Retirou', valor: (l) => l.retirado_por || '—' },
          { chave: 'valor', rotulo: 'Valor', numerica: true, forte: true,
            valor: (l) => (l.motivo === 'venda' ? dinheiro(l.valor) : '—') },
        ];

  // `t` e o mesmo `resumoDeSaidas` dos cartoes. So a venda soma no valor —
  // descarte e perda entram na contagem e ficam com travessao na coluna.
  const imprimir = () => gerarRelatorioPDF({
    titulo: 'Relatório de saídas de embriões',
    laboratorio: contexto.laboratorio,
    filtros: descreverFiltros(contexto),
    indicadores: [
      { rotulo: 'Saíram', valor: num(t.embrioes), detalhe: `${num(t.clientes)} cliente(s)` },
      { rotulo: 'Vendidos', valor: num(t.vendidos), detalhe: `de ${num(t.embrioes)} que saíram` },
      { rotulo: 'Receita das vendas', valor: dinheiro(t.valor),
        detalhe: t.vendidos > 0 ? `${dinheiro(t.valor / t.vendidos)} por embrião` : 'nenhuma venda' },
      { rotulo: 'Sem valor', valor: num(t.embrioes - t.vendidos),
        detalhe: 'descarte, perda, devolução' },
    ],
    grupos: {
      titulo: 'Por motivo',
      colunas: [
        { chave: 'm', rotulo: 'Motivo', valor: (g) => rotuloDoMotivo(g.motivo) },
        { chave: 'e', rotulo: 'Embriões', numerica: true, valor: (g) => num(g.embrioes) },
        { chave: 'v', rotulo: 'Valor', numerica: true, forte: true,
          valor: (g) => (g.valor > 0 ? dinheiro(g.valor) : '—') },
      ],
      linhas: t.porMotivo,
    },
    detalhe: { titulo: 'Saídas', colunas: COLUNAS, linhas },
  });

  if (linhas.length === 0) {
    return <Vazio mensagem="Nenhuma saída de embrião no período selecionado." />;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Destaque rotulo="Embriões que saíram" valor={num(t.embrioes)}
          detalhe={`${num(t.clientes)} cliente(s)`} />
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Indicador rotulo="Vendidos" valor={num(t.vendidos)}
            detalhe={t.embrioes > 0 ? `de ${num(t.embrioes)} que saíram` : ''} />
          {/* Só a venda soma. Descarte e perda têm valor nulo, e somá-los daria
              a impressão de que embrião jogado fora rende. */}
          <Indicador rotulo="Receita das vendas" valor={dinheiro(t.valor)}
            detalhe={t.vendidos > 0 ? `${dinheiro(t.valor / t.vendidos)} por embrião` : 'nenhuma venda'} />
          <Indicador rotulo="Sem valor" valor={num(t.embrioes - t.vendidos)}
            detalhe="descarte, perda, devolução" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Cartao titulo="Saídas por motivo"
          subtitulo="O que mais tira embrião do botijão.">
          <BarrasHorizontais larguraRotulo="w-28"
            dados={t.porMotivo.map(m => ({
              rotulo: rotuloDoMotivo(m.motivo), valor: m.embrioes, grupo: m,
            }))}
            detalhe={(d) => (d.grupo.valor > 0
              ? `${num(d.grupo.embrioes)} embriões · ${dinheiro(d.grupo.valor)}`
              : `${num(d.grupo.embrioes)} embriões`)}
          />
        </Cartao>

        <Cartao titulo="Saídas por cliente"
          subtitulo="Muda com o agrupamento escolhido acima.">
          <BarrasHorizontais
            dados={porDimensao.slice(0, 12).map(g => ({
              rotulo: g.chave === 'venda' || g.chave === 'descarte' || g.chave === 'perda'
                || g.chave === 'devolucao' || g.chave === 'envio' || g.chave === 'outro'
                ? rotuloDoMotivo(g.chave) : g.chave,
              valor: g.embrioes, grupo: g,
            }))}
            detalhe={(d) => (d.grupo.valor > 0
              ? `${num(d.grupo.embrioes)} embriões · ${dinheiro(d.grupo.valor)}`
              : `${num(d.grupo.embrioes)} embriões`)}
          />
        </Cartao>

        <Cartao titulo={`Saídas por ${granularidade === 'mes' ? 'mês' : 'dia'}`}
          subtitulo="Em ordem cronológica — a leitura aqui é a evolução.">
          <BarrasHorizontais larguraRotulo="w-16"
            dados={porPeriodo.map(p => ({
              rotulo: rotuloDePeriodo(p.chave), valor: p.linhas || 0, grupo: p,
            }))}
            detalhe={(d) => (d.grupo._valor_venda > 0
              ? `${dinheiro(d.grupo._valor_venda)} em vendas`
              : 'sem venda no período')}
          />
        </Cartao>
      </div>

      <Tabela
        titulo="Saídas"
        subtitulo={`${num(linhas.length)} no período`}
        colunas={COLUNAS}
        linhas={linhas.map(l => ({ ...l, chave: l.embryo_id }))}
        acao={<BotaoPDF onClick={imprimir} />}
      />
    </div>
  );
};

export default PainelSaidas;
